import React from "react";
import Checkbox from "../../ui/Checkbox";
import FieldLabel from "../../ui/FieldLabel";
import MenuSelect from "../../ui/MenuSelect";
import Tooltip from "../../ui/Tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/Collapsible";
import { usePhysicsStore } from "../../../stores/physicsStore";
import { startSimulationFromCurrentFiles, stopSimulation } from "../../../physics/mujocoBridge";
import { logger } from "../../../utils/logger";
import { Row } from "./PhysicsPanelControls";
import PhysicsPanelMappingDebugExpanded from "./PhysicsPanelMappingDebugExpanded";
import * as THREE from "three";

export default function PhysicsDevToolsSection({
  engine,
  exportWorkerXml,
  isRunning,
  loadMode,
  mapped,
  poseNoGroupConversion,
  probeName,
  setLoadMode,
  setPoseNoGroupConversion,
  setProbeName,
  setShowDevToolsSection,
  setShowMappingDebug,
  showDevTools,
  showDevToolsSection,
  showMappingDebug,
  total,
  wasmRuntimeInfo,
  worker,
}) {
  return showDevTools ? (
    <Collapsible open={showDevToolsSection} onOpenChange={setShowDevToolsSection}>
      <div className="h-px bg-gray-700 my-2" />
      <CollapsibleTrigger quiet className="w-full text-xs text-gray-200 justify-between">
        Dev Tools
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Row label="Engine">
          <span style={{ color: engine === "mujoco" ? "#4ade80" : "#f87171" }}>{engine}</span>
        </Row>
        <Row label="MuJoCo WASM">
          <span className="text-gray-200 text-sm select-none">{wasmRuntimeInfo.label}</span>
        </Row>
        <Row label="Mapped">
          <span style={{ color: "#60a5fa" }}>
            {mapped}/{total}
          </span>
        </Row>
        <Row label="Load Mode">
          <MenuSelect
            size="compact"
            className="min-w-[8.5rem]"
            value={loadMode}
            onChange={async (mode) => {
              setLoadMode(mode);
              try {
                if (isRunning) {
                  await stopSimulation();
                  setTimeout(async () => {
                    await startSimulationFromCurrentFiles();
                  }, 50);
                }
              } catch (err) {
                logger.error("Failed to switch load mode:", err);
              }
            }}
            options={[
              { value: "full", label: "Full" },
              { value: "noInertials", label: "No Inertials" },
              { value: "noMeshes", label: "No Meshes" },
              { value: "minimal", label: "Minimal" },
            ]}
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Physics load mode"
          />
        </Row>
        <Row label="Viewer Conv">
          <Tooltip content={<span>Toggle applying robotGroup world rotation to MuJoCo poses</span>}>
            <button
              className="px-2 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 text-xs"
              onClick={() => {
                try {
                  const next = !poseNoGroupConversion;
                  setPoseNoGroupConversion(next);
                  logger.debug(
                    "[Physics UI] pose world->viewer rotation",
                    next ? "DISABLED" : "ENABLED"
                  );
                  // If physics is running, restart it to apply the new coordinate system
                  if (isRunning) {
                    logger.debug(
                      "[Physics UI] Restarting physics to apply coordinate system change"
                    );
                    // Set flag to prevent baseline sync during transition
                    usePhysicsStore.setState({ coordinateSystemChanging: true });
                    stopSimulation()
                      .then(() => {
                        setTimeout(() => {
                          usePhysicsStore.setState({ coordinateSystemChanging: false });
                          startSimulationFromCurrentFiles();
                        }, 100);
                      })
                      .catch((err) => {
                        usePhysicsStore.setState({ coordinateSystemChanging: false });
                        logger.warn("Failed restarting physics for pose conversion flag", err);
                      });
                  }
                } catch (e) {
                  logger.warn("Failed toggling pose conversion flag", e);
                }
              }}
            >
              {poseNoGroupConversion ? "Group Rot: OFF" : "Group Rot: ON"}
            </button>
          </Tooltip>
        </Row>
        <div className="h-px bg-gray-700 my-2" />
        {/* Debug controls */}
        <div className="px-3 py-2 space-y-2 border border-gray-700 rounded">
          <div className="flex items-center justify-between">
            <Checkbox
              label="Pose debug logs"
              className="text-sm text-gray-200 w-auto px-0 py-0"
              onChange={(e) => {
                const enabled = e.target.checked;
                try {
                  window.__debugPhysicsPoses = enabled;
                } catch {
                  // Best-effort debug flag update.
                }
                if (worker) {
                  try {
                    worker.postMessage({
                      type: "debugPhysics",
                      payload: { enabled },
                    });
                  } catch (error) {
                    logger.warn("[PhysicsPanel] Failed to update physics debug worker flag", error);
                  }
                }
                logger.debug("[Physics UI] debug poses:", enabled);
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              className="bg-gray-800 text-gray-200 border border-gray-600 rounded px-2 py-1 text-sm flex-1"
              placeholder="Link name to probe (e.g., forearm_link)"
              value={probeName}
              onChange={(e) => setProbeName(e.target.value)}
            />
            <button
              className="px-2 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 text-xs"
              onClick={() => {
                try {
                  const scene = window.threeDebug?.scene;
                  if (!scene) {
                    logger.warn("[Probe] threeDebug.scene not available");
                    return;
                  }
                  const obj = scene.getObjectByName(probeName);
                  if (!obj) {
                    logger.warn("[Probe] object not found:", probeName);
                    return;
                  }
                  if (!THREE || !THREE.Vector3) {
                    logger.warn("[Probe] THREE not available");
                    return;
                  }

                  // Collect ancestor world scales
                  const s = new THREE.Vector3();
                  const scales = [];
                  let p = obj;
                  while (p) {
                    if (p.getWorldScale) {
                      p.getWorldScale(s);
                      scales.push({ name: p.name || p.uuid, scale: s.toArray() });
                    }
                    p = p.parent;
                  }

                  // Container world transform
                  const wp = new THREE.Vector3();
                  const wq = new THREE.Quaternion();
                  obj.getWorldPosition(wp);
                  if (obj.getWorldQuaternion) obj.getWorldQuaternion(wq);

                  // Find same-named descendants (excluding the container itself)
                  const matches = [];
                  if (typeof obj.traverse === "function") {
                    obj.traverse((child) => {
                      if (!child || child === obj) return;
                      if (child.name === probeName) {
                        const cwp = new THREE.Vector3();
                        const cwq = new THREE.Quaternion();
                        if (child.getWorldPosition) child.getWorldPosition(cwp);
                        if (child.getWorldQuaternion) child.getWorldQuaternion(cwq);
                        const lp = child.position
                          ? [child.position.x, child.position.y, child.position.z]
                          : null;
                        const lq = child.quaternion
                          ? [
                              child.quaternion.x,
                              child.quaternion.y,
                              child.quaternion.z,
                              child.quaternion.w,
                            ]
                          : null;
                        matches.push({
                          node: child,
                          localPos: lp,
                          localQuat: lq,
                          worldPos: cwp.toArray(),
                          worldQuat: cwq.toArray(),
                          childNames: (child.children || [])
                            .map((c) => c && c.name)
                            .filter(Boolean),
                        });
                      }
                    });
                  }

                  logger.debug("[Probe] result", {
                    container: obj,
                    containerWorldPos: wp.toArray(),
                    containerWorldQuat: wq.toArray(),
                    sameNamedDescendants: matches,
                    ancestorScales: scales,
                  });
                } catch (e) {
                  logger.warn("[Probe] failed:", e);
                }
              }}
            >
              Probe
            </button>
          </div>
        </div>
        <div className="h-px bg-gray-700 my-2" />
        <div className="flex items-center justify-between px-3">
          <button
            className="px-3 py-1 rounded border border-blue-600 text-blue-200 hover:bg-blue-900"
            onClick={() => exportWorkerXml()}
          >
            Export Physics XML
          </button>
          <FieldLabel as="span" className="select-none">
            Use console for MuJoCo logs
          </FieldLabel>
        </div>
        <div className="h-px bg-gray-700 my-2" />
        <div className="px-3 flex items-center justify-between">
          <Checkbox
            label="Show Mapping Debug"
            checked={showMappingDebug}
            onChange={(e) => setShowMappingDebug(e.target.checked)}
            className="text-sm text-gray-200 w-auto px-0 py-0"
          />
        </div>
        {showMappingDebug && <PhysicsPanelMappingDebugExpanded />}
      </CollapsibleContent>
    </Collapsible>
  ) : null;
}
